import { Component, OnDestroy, OnInit } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { AppointmentsViewEnum } from 'src/app/components/contact-status/appointments/appointmentsViewEnum';
import { ISpecificBrickSettingsComponent } from 'src/app/components/dashboard/brick-settings/brick-specific-type-settings/ISpecificBrickSettingsComponent';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import { ShortcutTypeDirectory } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeDirectory';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import {
   ChangeAutoSearchColumn,
   ChangeAutomaticFilterOnSkillOrganisationAssociations,
   ChangeAutomaticRestrictForIncomingCaller,
   ChangeClearSearchBoxOnIncomingCall,
   ChangeDefaultDirectoryId,
   ChangeDirectoryCallableColumns,
   ChangeDirectoryDefaultCallColumn,
   ChangeDirectoryDefaultSortColumn,
   ChangeDirectorySearchableColumns,
   ChangeDirectorySearchablePhoneticColumns,
   ChangeMinLengthToStartFiltering,
   ChangeRetainFilterOnCallFinish,
   ChangeSchedulerSelectedView,
   ChangeSchedulerShowBusinessHours,
   ChangeStartNewCallOnDoubleClick,
} from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-settings/directory-settings.actions';
import { DirectorySettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import { DirectoryOrganisationDataSelectors } from 'src/app/store/samwin/directory/directory-organisation-data/directory-organisation-data.selectors';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { OrganisationEntryRemoteDto } from 'src/generated/swagger';

@Component({
   selector: 'samweb-directory-brick-settings',
   templateUrl: './directory-brick-settings.component.html',
   styleUrls: ['./directory-brick-settings.component.scss'],
})
export class DirectoryBrickSettingsComponent implements OnInit, OnDestroy, ISpecificBrickSettingsComponent {
   constructor(private store: Store, private translateService: TranslateService) {}
   public brickInstanceId: string;

   public automaticFilterOnSkillOrganisationAssociations$: Observable<boolean>;
   public automaticRestrictForIncomingCaller$: Observable<boolean>;
   public minLengthToStartFiltering$: Observable<number>;
   public retainFilterOnCallFinish$: Observable<boolean>;
   public startNewCallOnDoubleClick$: Observable<boolean>;
   public clearSearchBoxOnIncomingCall$: Observable<boolean>;
   public searchableColumns$: Observable<DirectoryColumn[]>;
   public searchablePhoneticColumns$: Observable<DirectoryColumn[]>;
   public defaultCallableColumn$: Observable<DirectoryColumn>;
   public callableColumns$: Observable<DirectoryColumn[]>;
   public defaultSortableColumn$: Observable<DirectoryColumn>;
   public selectedDirectory$: Observable<OrganisationEntryRemoteDto>;
   public appointmentViews: AppointmentsViewEnum[] = _(AppointmentsViewEnum.values()).value();
   public selectedAppointmentView$: Observable<AppointmentsViewEnum>;
   public appointmentsShowBusinessHours$: Observable<boolean>;
   public autoSearchColumn$: Observable<DirectoryColumn>;
   private destroy$ = new Subject();

   @Select(DirectorySettingsState.getAllPossibleSearchableColumns)
   public possibleSearchableColumns$: Observable<DirectoryColumn[]>;

   @Select(DirectorySettingsState.getAllPossibleSearchablePhoneticColumns)
   public possiblePhoneticSearchableColumns$: Observable<DirectoryColumn[]>;

   @Select(DirectoryStructureState.availableDirectoryColumns)
   public availableColumns$: Observable<DirectoryColumn[]>;

   @Select(DirectoryOrganisationDataSelectors.getDirectoryRestrictionEntries)
   public directoryStructureData$: Observable<OrganisationEntryRemoteDto[]>;

   @Select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.CCO_PERM_EDIT_CONFIG_KEY_CONF))
   public isFeatureEnabled$: Observable<boolean>;

   @Select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_ATTENDANT))
   public isAttendant$: Observable<boolean>;

   public availableHotkeyTypes = this.getValues();

   public defaultDial: IconDefinition = WellKnownIconsEnum.DEFAULT_DIAL.icon;

   private getValues(): ShortcutTypeDirectory[] {
      return _(ShortcutTypeDirectory.values())
         .orderBy((shortcut) => this.translateService.instant(shortcut.translationKey, { columnToDial: shortcut.columnName }))
         .value();
   }

   ngOnInit() {
      this.automaticFilterOnSkillOrganisationAssociations$ = this.store
         .select(DirectorySettingsState.getAutomaticFilterOnSkillOrganisationAssociations)
         .pipe(map((fn) => fn(this.brickInstanceId)));
      this.automaticRestrictForIncomingCaller$ = this.store
         .select(DirectorySettingsState.getAutomaticRestrictForIncomingCaller)
         .pipe(map((fn) => fn(this.brickInstanceId)));
      this.minLengthToStartFiltering$ = this.store.select(DirectorySettingsState.getMinLengthToStartFiltering).pipe(map((fn) => fn(this.brickInstanceId)));
      this.retainFilterOnCallFinish$ = this.store.select(DirectorySettingsState.getRetainFilterOnCallFinish).pipe(map((fn) => fn(this.brickInstanceId)));
      this.startNewCallOnDoubleClick$ = this.store.select(DirectorySettingsState.getStartNewCallOnDoubleClick).pipe(map((fn) => fn(this.brickInstanceId)));
      this.clearSearchBoxOnIncomingCall$ = this.store
         .select(DirectorySettingsState.getClearSearchBoxOnIncomingCall)
         .pipe(map((fn) => fn(this.brickInstanceId)));
      this.searchableColumns$ = this.store.select(DirectorySettingsState.getDirectorySearchableColumns).pipe(map((fn) => fn(this.brickInstanceId)));
      this.searchablePhoneticColumns$ = this.store
         .select(DirectorySettingsState.getDirectorySearchablePhoneticColumns)
         .pipe(map((fn) => fn(this.brickInstanceId)));
      this.callableColumns$ = this.store.select(DirectorySettingsState.getCallableColumns).pipe(map((fn) => fn(this.brickInstanceId)));
      this.defaultCallableColumn$ = this.store.select(DirectorySettingsState.getDefaultCallColumn).pipe(map((fn) => fn(this.brickInstanceId)));
      this.defaultSortableColumn$ = this.store.select(DirectorySettingsState.getDefaultSortColumn).pipe(map((fn) => fn(this.brickInstanceId)));
      this.autoSearchColumn$ = this.store.select(DirectorySettingsState.getDefaultListeningColumn).pipe(map((fn) => fn(this.brickInstanceId)));
      this.selectedDirectory$ = this.store.select(DirectorySettingsState.getDefaultSelectedDirectory).pipe(map((fn) => fn(this.brickInstanceId)));
      this.selectedAppointmentView$ = this.store.select(DirectorySettingsState.getSchedulerSelectedView).pipe(
         map((getValue) => getValue(this.brickInstanceId)),
         map((key) => this.appointmentViews[key])
      );
      this.appointmentsShowBusinessHours$ = this.store
         .select(DirectorySettingsState.getSchedulerShowBusinessHours)
         .pipe(map((getValue) => getValue(this.brickInstanceId)));

      combineLatest([this.autoSearchColumn$, this.searchableColumns$])
         .pipe(
            filter(([autoSearchColumnKey, searchableColumns]) => {
               return autoSearchColumnKey && !searchableColumns.includes(autoSearchColumnKey);
            }),
            takeUntil(this.destroy$)
         )
         .subscribe(() => {
            this.store.dispatch(new ChangeAutoSearchColumn(this.brickInstanceId, null));
         });
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   changeAutomaticFilterOnSkillOrganisationAssociations(checked: boolean) {
      this.store.dispatch(new ChangeAutomaticFilterOnSkillOrganisationAssociations(this.brickInstanceId, checked));
   }

   changeAutomaticRestrictForIncomingCaller(checked: boolean) {
      this.store.dispatch(new ChangeAutomaticRestrictForIncomingCaller(this.brickInstanceId, checked));
   }

   changeRetainFilterOnCallFinish$(checked: boolean) {
      this.store.dispatch(new ChangeRetainFilterOnCallFinish(this.brickInstanceId, checked));
   }

   changeStartNewCallOnDoubleClick(checked: boolean) {
      this.store.dispatch(new ChangeStartNewCallOnDoubleClick(this.brickInstanceId, checked));
   }

   changeClearSearchBoxOnIncomingCall(checked: boolean) {
      this.store.dispatch(new ChangeClearSearchBoxOnIncomingCall(this.brickInstanceId, checked));
   }

   changeMinLengthToStartFiltering$(value: number) {
      this.store.dispatch(new ChangeMinLengthToStartFiltering(this.brickInstanceId, value));
   }

   searchableColumnsChanged(selectedColumnKeys: DirectoryColumn[]) {
      this.store.dispatch(
         new ChangeDirectorySearchableColumns(
            this.brickInstanceId,
            _.map(selectedColumnKeys, (x) => x.directoryColumnKey)
         )
      );
   }

   searchablePhoneticColumnsChanged(selectedColumnKeys: DirectoryColumn[]) {
      this.store.dispatch(
         new ChangeDirectorySearchablePhoneticColumns(
            this.brickInstanceId,
            _.map(selectedColumnKeys, (x) => x.directoryColumnKey)
         )
      );
   }

   callableColumnsChanged(selectedCallableColumnKeys: DirectoryColumn[]) {
      this.store.dispatch(
         new ChangeDirectoryCallableColumns(
            this.brickInstanceId,
            _.map(selectedCallableColumnKeys, (x) => x.directoryColumnKey)
         )
      );
   }

   changeAutoSearchColumn(value: DirectoryColumn) {
      this.store.dispatch(new ChangeAutoSearchColumn(this.brickInstanceId, value.directoryColumnKey));
   }

   changeDefaultCallColumn(selectedDefaultCallColumnKey: DirectoryColumn) {
      this.store.dispatch(new ChangeDirectoryDefaultCallColumn(this.brickInstanceId, selectedDefaultCallColumnKey.directoryColumnKey));
   }

   changeDefaultSortColumn(selectedDefaultCallColumnKey: DirectoryColumn) {
      this.store.dispatch(new ChangeDirectoryDefaultSortColumn(this.brickInstanceId, selectedDefaultCallColumnKey.directoryColumnKey));
   }

   changeSelectedDirectory(selectedDirectory: OrganisationEntryRemoteDto) {
      this.store.dispatch(new ChangeDefaultDirectoryId(this.brickInstanceId, selectedDirectory.id));
   }

   changeAppointmentView(selectedView: AppointmentsViewEnum) {
      this.store.dispatch(new ChangeSchedulerSelectedView(this.brickInstanceId, selectedView.value));
   }

   changeAppointmentsShowBusinessHours(checked: boolean) {
      this.store.dispatch(new ChangeSchedulerShowBusinessHours(this.brickInstanceId, checked));
   }
}
